import { TTabs } from 'widgets/global/Tabs';

type TButton = ReturnType<typeof import('widgets/global/Button').default>;
type TButtonInstance = InstanceType<TButton>;

export type TTabPanelChangeEvent = {
    activePanelName: string;
    previousActivePanelName: string;
};

/**
 * @description Base Tabs implementation
 * @param Tabs - widget for extending
 * @returns ShopTheLookTabs widget
 */
function ShopTheLookTabsClassCreator(Tabs: TTabs) {
    /**
     * @category widgets
     * @subcategory [product]
     * @class ShopTheLookTabs
     * @augments Widget
     * @property {string} data-widget - Widget name `shopTheLookTabs`
     * @classdesc Represents tabbed contents displaying for Shop The Look functionality
     * Has the next features:
     * 1. Allow to have tabs switcher inside tab panel
     */
    class ShopTheLookTabs extends Tabs {
        /**
         * @description Widget initialization logic
         */
        init() {
            super.init();

            const tabList = this.ref('tablist').get()?.cloneNode(true) as HTMLElement;

            if (tabList) {
                tabList.removeAttribute('hidden');
                this.ref('self').get()?.querySelectorAll<HTMLElement>('[data-ref="innerTabs"]').forEach((tab) => {
                    tab.innerHTML = tabList.outerHTML;
                });
            }
        }

        /**
         * @description Handles keydown event from inner tabs buttons
         * @param _ - HTMLElement
         * @param event - KeyboardEvent
         */
        handleInnerTabKeydown(_: HTMLElement, event: KeyboardEvent) {
            this.handleKeydown(_, event);
        }

        /**
         * @description Toggle tab button
         * @param id - panel ID
         * @param isActive - Is current should be active
         */
        toggleButton(id: string, isActive: boolean) {
            const buttonId = `button-${id}`;
            const toggleFn = (child: TButtonInstance) => {
                if (child.id === buttonId) {
                    if (isActive) {
                        child.activate();
                        child.select();
                    } else {
                        child.deactivate();
                        child.unselect();
                    }
                } else {
                    child.eachChild(toggleFn);
                }
            };

            this.eachChild<TButtonInstance>(toggleFn);
        }

        /**
         * @description Activate panel
         * @param id - panel ID
         * @param activeTabEl - active tab element
         * @param isTargetTab - is target tab
         */
        focusInnerButton(id: string, activeTabEl: HTMLElement|undefined, isTargetTab: boolean) {
            const focusFn = (button: TButtonInstance) => {
                if (button.id === id) {
                    if (isTargetTab) {
                        const buttonEl = button.ref('self').get();

                        // Check if activeTabEl is parent of buttonEl
                        if (activeTabEl && buttonEl && activeTabEl.contains(buttonEl)) {
                            button.focus();
                        }

                        button.setTabIndex();
                    } else {
                        button.unsetTabIndex();
                    }
                } else {
                    button.eachChild(focusFn);
                }
            };

            this.eachChild<TButtonInstance>(focusFn);
        }

        /**
         * @description Sets focus to panel with given name
         * <br>Uses `roving focus` accessibility feature
         * <br>https://www.w3.org/TR/wai-aria-practices/#kbd_roving_tabindex
         * @param tab Tab name to set focus to
         */
        setFocusToTab(tab: string) {
            if (!tab) {
                return;
            }

            this.focusedTab = tab;

            if (this.prefs().autoActivation) {
                this.activatePanel(tab);
            }

            const activeTabEl = this.ref(tab).get();

            if (this.panelNames) {
                this.panelNames.forEach(id => {
                    const isTargetTab = id === tab;

                    this.focusInnerButton(`button-${id}`, activeTabEl, isTargetTab);
                });
            }
        }
    }

    return ShopTheLookTabs;
}

export type TShopTheLookTabs = ReturnType<typeof ShopTheLookTabsClassCreator>;

export type TShopTheLookTabsInstance = InstanceType<TShopTheLookTabs>;

export default ShopTheLookTabsClassCreator;
